
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { Link, useLocation } from "react-router-dom";
import { prefetchRoute } from "@/utils/prefetchRoutes";
import { WaitlistWidget } from "@/components/WaitlistWidget";

const Navbar = () => {
  const isMobile = useIsMobile();
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  React.useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      setIsScrolled(offset > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white shadow-sm py-3"
          : "bg-white py-5"
      }`}
    >
      <div className="container mx-auto flex items-center justify-between">
        <div className="flex items-center">
          <Link to="/" className="flex items-center">
            <img 
              src="/lovable-uploads/3b945d96-8087-4b84-9ca1-1de1f3e98362.png" 
              alt="Savyi Logo" 
              className="h-8 md:h-10" 
            />
          </Link>
        </div>

        {isMobile ? (
          <MobileMenu currentPath={location.pathname} />
        ) : (
          <div className="flex items-center gap-8">
            <NavLinks currentPath={location.pathname} />
            <div className="flex gap-4">
              <Button variant="outline" className="rounded-full px-6" asChild>
                <a 
                  href="https://savyi.co/successhtml.html"
                  onMouseEnter={() => prefetchRoute('/dashboard')}
                  onFocus={() => prefetchRoute('/dashboard')}
                >Log in</a>
              </Button>
              <WaitlistWidget variant="inline" buttonText="Get Early Access" />
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

const NavLinks = ({ 
  currentPath,
}: { 
  currentPath: string;
}) => (
  <div className="flex gap-6">
    <Link 
      to="/#features" 
      className="text-gray-700 hover:text-savyi-purple transition-colors"
    >
      Features
    </Link>
    <Link 
      to="/how-it-works" 
      className={`transition-colors ${
        currentPath === "/how-it-works"
          ? "text-savyi-purple font-medium" 
          : "text-gray-700 hover:text-savyi-purple"
      }`}
      onMouseEnter={() => prefetchRoute('/how-it-works')}
      onFocus={() => prefetchRoute('/how-it-works')}
    >
      How It Works
    </Link>
    <Link 
      to="/timeline" 
      className={`transition-colors ${
        currentPath === "/timeline" 
          ? "text-savyi-purple font-medium" 
          : "text-gray-700 hover:text-savyi-purple"
      }`}
      onMouseEnter={() => prefetchRoute('/timeline')}
      onFocus={() => prefetchRoute('/timeline')}
    >
      Roadmap
    </Link>
    <Link 
      to="/demo" 
      className={`transition-colors ${
        currentPath === "/demo" 
          ? "text-savyi-purple font-medium" 
          : "text-gray-700 hover:text-savyi-purple"
      }`}
      onMouseEnter={() => prefetchRoute('/demo')}
      onFocus={() => prefetchRoute('/demo')}
    >
      Demo
    </Link>
    <Link 
      to="/bright-plan" 
      className={`transition-colors ${
        currentPath === "/bright-plan" || currentPath === "/early-access"
          ? "text-savyi-purple font-medium" 
          : "text-gray-700 hover:text-savyi-purple"
      }`}
      onMouseEnter={() => prefetchRoute('/bright-plan')}
      onFocus={() => prefetchRoute('/bright-plan')}
    >
      Bright Plan
    </Link>
  </div>
);

const MobileMenu = ({ 
  currentPath,
}: { 
  currentPath: string;
}) => (
  <Sheet>
    <SheetTrigger asChild>
      <Button variant="ghost" size="icon" className="md:hidden">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="4" x2="20" y1="12" y2="12" />
          <line x1="4" x2="20" y1="6" y2="6" />
          <line x1="4" x2="20" y1="18" y2="18" />
        </svg>
      </Button>
    </SheetTrigger>
    <SheetContent>
      <div className="flex flex-col mt-10 gap-6">
        <div className="flex flex-col gap-6">
          <Link 
            to="/#features" 
            className="text-gray-700 hover:text-savyi-purple transition-colors"
          >
            Features
          </Link>
          <Link 
            to="/how-it-works" 
            className={`transition-colors ${
              currentPath === "/how-it-works"
                ? "text-savyi-purple font-medium" 
                : "text-gray-700 hover:text-savyi-purple"
            }`}
            onMouseEnter={() => prefetchRoute('/how-it-works')}
            onFocus={() => prefetchRoute('/how-it-works')}
          >
            How It Works
          </Link>
          <Link 
            to="/timeline" 
            className={`transition-colors ${
              currentPath === "/timeline" 
                ? "text-savyi-purple font-medium" 
                : "text-gray-700 hover:text-savyi-purple"
            }`}
            onMouseEnter={() => prefetchRoute('/timeline')}
            onFocus={() => prefetchRoute('/timeline')}
          >
            Roadmap
          </Link>
          <Link 
            to="/demo" 
            className={`transition-colors ${
              currentPath === "/demo" 
                ? "text-savyi-purple font-medium" 
                : "text-gray-700 hover:text-savyi-purple"
            }`}
            onMouseEnter={() => prefetchRoute('/demo')}
            onFocus={() => prefetchRoute('/demo')}
          >
            Demo
          </Link>
          <Link 
            to="/bright-plan" 
            className={`transition-colors ${
              currentPath === "/bright-plan" || currentPath === "/early-access"
                ? "text-savyi-purple font-medium" 
                : "text-gray-700 hover:text-savyi-purple"
            }`}
            onMouseEnter={() => prefetchRoute('/bright-plan')}
            onFocus={() => prefetchRoute('/bright-plan')}
          >
            Bright Plan
          </Link>
        </div>
        <div className="flex flex-col gap-4 mt-4">
          <Button variant="outline" className="rounded-full" asChild>
            <a 
              href="https://savyi.co/successhtml.html"
              onMouseEnter={() => prefetchRoute('/dashboard')}
              onFocus={() => prefetchRoute('/dashboard')}
            >Log in</a>
          </Button>
          <WaitlistWidget variant="inline" buttonText="Get Early Access" />
        </div>
      </div>
    </SheetContent>
  </Sheet>
);

export default Navbar;
